import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { reportingService } from '../../../services/reportingService';

const VehicleTypeDetailModal = ({ vehicleType, dateRange, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState(null);

  useEffect(() => {
    if (vehicleType?.type) {
      loadVehicleTypeDetails();
    }
  }, [vehicleType]);

  const loadVehicleTypeDetails = async () => {
    setLoading(true);
    try {
      const data = await reportingService?.getVehicleTypeServiceBreakdown(
        vehicleType?.type,
        dateRange?.startDate,
        dateRange?.endDate
      );
      setDetails(data);
    } catch (error) {
      console.error('Error loading vehicle type details:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return `$${value?.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getIconForType = (type) => {
    return type === 'Auto' ? 'Car' : 'Bike';
  };

  const getColorForType = (type) => {
    return type === 'Auto' ? 'text-primary' : 'text-success';
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-elevation-3 max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className={`p-2 ${vehicleType?.type === 'Auto' ? 'bg-primary/10' : 'bg-success/10'} rounded-lg`}>
              <Icon name={getIconForType(vehicleType?.type)} size={24} className={getColorForType(vehicleType?.type)} />
            </div>
            <div>
              <h2 className="text-xl font-heading font-bold text-foreground">
                Servicios para {vehicleType?.type}s
              </h2>
              <p className="text-sm text-muted-foreground">
                Desglose por Categoría de Servicio
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <Icon name="Loader2" className="w-8 h-8 text-primary animate-spin" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Summary Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-primary/5 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="DollarSign" size={18} className="text-primary" />
                    <span className="text-sm text-muted-foreground">Ingresos Totales</span>
                  </div>
                  <p className="text-2xl font-bold text-foreground">
                    {formatCurrency(details?.totalRevenue || 0)}
                  </p>
                </div>
                <div className="bg-success/5 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="Wrench" size={18} className="text-success" />
                    <span className="text-sm text-muted-foreground">Total Servicios</span>
                  </div>
                  <p className="text-2xl font-bold text-foreground">
                    {details?.totalServices || 0}
                  </p>
                </div>
                <div className="bg-warning/5 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="TrendingUp" size={18} className="text-warning" />
                    <span className="text-sm text-muted-foreground">Precio Promedio</span>
                  </div>
                  <p className="text-2xl font-bold text-foreground">
                    {formatCurrency(details?.averageServicePrice || 0)}
                  </p>
                </div>
              </div>

              {/* Service Categories */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Icon name="List" size={20} />
                  Categorías de Servicio
                </h3>
                <div className="space-y-3">
                  {details?.serviceCategories?.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      No hay datos de servicios en este período
                    </p>
                  ) : (
                    details?.serviceCategories?.map((category, index) => (
                      <div
                        key={index}
                        className="bg-muted/30 rounded-lg p-4 hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-primary/10 rounded-lg">
                              <Icon name="Wrench" size={18} className="text-primary" />
                            </div>
                            <div>
                              <p className="font-semibold text-foreground">
                                {category?.categoryName || 'Sin categoría'}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {category?.serviceCount} servicio{category?.serviceCount !== 1 ? 's' : ''}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-success">
                              {formatCurrency(category?.totalRevenue)}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Promedio: {formatCurrency(category?.averagePrice)}
                            </p>
                          </div>
                        </div>
                        
                        {/* Progress Bar */}
                        <div className="w-full bg-muted rounded-full h-2">
                          <div
                            className="bg-primary h-2 rounded-full transition-all"
                            style={{ width: `${category?.revenuePercentage || 0}%` }}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 text-right">
                          {category?.revenuePercentage?.toFixed(1)}% del total
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Top Services */}
              {details?.topServices?.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Icon name="Star" size={20} />
                    Servicios Más Solicitados
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {details?.topServices?.map((service, index) => (
                      <div
                        key={index}
                        className="bg-muted/30 rounded-lg p-3 flex items-center justify-between"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-lg font-bold text-primary">
                            #{index + 1}
                          </span>
                          <div>
                            <p className="text-sm font-medium text-foreground">
                              {service?.serviceName}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {service?.count} veces
                            </p>
                          </div>
                        </div>
                        <p className="text-sm font-semibold text-success">
                          {formatCurrency(service?.totalRevenue)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VehicleTypeDetailModal;